import {
  StoryAISummaryFeedback,
  trackStoryAISummaryFeedback,
  trackUserBaseV2Selector,
} from '@fullstory/analytics';
import { useNotifications } from '@fullstory/react-hooks';
import { GRPCRequestConfig, useRequestMutationOptions } from '@fullstory/requests';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  SendSummariesFeedbackRequest,
  AISummaryFeedbackDetails,
  FeedbackServicePublic,
} from '@fullstory/proto/fs/services/webber/webber.proto';

interface UseAISummaryFeedbackProps {
  /** Summary text that was generated */
  aiSummary: string;
  /** Callback to close the modal after feedback is submitted */
  onClose?: () => void;
}
export const sendSummaryFeedback = (
  request: SendSummariesFeedbackRequest,
): GRPCRequestConfig<SendSummariesFeedbackRequest, {}> => ({
  request: {
    grpcFunction: FeedbackServicePublic.SendSummariesFeedback,
    body: request,
  },
});

/**
 * Hook that provides an implementation of onFeedback for AISummaryFeedbackModal
 * Tracks analytics event with all feedback details and sends feedback to slack
 */
export const useAISummaryFeedback = ({ aiSummary, onClose }: UseAISummaryFeedbackProps) => {
  const userAnalytics = useSelector(trackUserBaseV2Selector);
  const notification = useNotifications();

  const mutationOptions = useRequestMutationOptions(sendSummaryFeedback);

  const { mutate } = useMutation(mutationOptions);

  return useCallback(
    (isHelpful: boolean, details?: Partial<StoryAISummaryFeedback>) => {
      // Track the feedback event
      let analyticsEvent = {
        ...userAnalytics,
        aiSummary,
        helpful: isHelpful,
      };

      // If not helpful, include the detailed feedback
      if (!isHelpful && details) {
        analyticsEvent = {
          ...analyticsEvent,
          ...details,
        };
      }

      trackStoryAISummaryFeedback(analyticsEvent);

      if (!isHelpful) {
        const feedbackDetails: AISummaryFeedbackDetails = {
          tooLongOrGeneral: details?.tooLongOrGeneral || false,
          inaccurateInfo: details?.inaccurateInfo || false,
          missedKeyPoints: details?.missedKeyPoints || false,
          missedEntireSession: details?.missedEntireSession || false,
          other: details?.other || false,
          additionalFeedback: details?.additionalFeedback || '',
        };

        const request: SendSummariesFeedbackRequest = {
          orgId: userAnalytics.org_id,
          aiSummary,
          isHelpful,
          feedbackDetails: isHelpful ? null : feedbackDetails,
          sessionUrl: userAnalytics.current_session || '',
        };

        mutate(request);
      }
      // We don't need to wait for the response from the server, so we can close the modal immediately.
      // Feedback is still sent to FS even if we fail sending to slack
      notification.success('Feedback submitted');
      onClose?.();
    },
    [aiSummary, mutate, notification, onClose, userAnalytics],
  );
};
